import { useEffect, useState, useRef } from "react";
import * as signalR from "@microsoft/signalr";
import { useDispatch } from "react-redux";
import { chargingTransctionActions } from "../store/actions/transactions/chargingTransaction.actions";
import { signalRKeysEnum } from "../enums/signalR/signalRKeysEnum";

export const useSignalR = (token) => {
  const [connection, setConnection] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const connectionRef = useRef(null);

  const dispatch = useDispatch();

  // Get the SignalR URL from environment variables
  const SIGNAL_R_URL = process.env.REACT_APP_SIGNAL_R_URL;

  useEffect(() => {
    const connectSignalR = async () => {
      const newConnection = new signalR.HubConnectionBuilder()
        .withUrl(SIGNAL_R_URL, {
          accessTokenFactory: () => token || "",
        })
        .configureLogging(signalR.LogLevel.Debug) // Enable detailed logging
        .withAutomaticReconnect()
        .build();

      // Set up listeners BEFORE starting connection to avoid missing messages
      initializeMessagesListener(newConnection);

      newConnection.onclose(() => {
        setIsConnected(false);
        console.log("SignalR connection closed");
      });
      
      newConnection.onreconnected(() => {
        setIsConnected(true);
        // Re-initialize listeners on reconnect to ensure they're set up
        initializeMessagesListener(newConnection);
        console.log("SignalR reconnected");
      });

      try {
        await newConnection.start();
        setIsConnected(true);
        console.log("*** SignalR Connected ***");
      } catch (error) {
        console.error("SignalR connection failed: ", error);
        setIsConnected(false);
      }

      setConnection(newConnection);
      connectionRef.current = newConnection;
    };

    if (SIGNAL_R_URL) {
      connectSignalR();
    }

    return () => {
      if (connectionRef.current) {
        connectionRef.current.stop();
        console.log("SignalR connection stopped");
        connectionRef.current = null;
      }
    };
  }, [token, SIGNAL_R_URL]);

  const initializeMessagesListener = (newConnection) => {
    // Remove old listeners to avoid duplicates
    newConnection.off(signalRKeysEnum.RefereshChargingTransacitonData);
    newConnection.off(signalRKeysEnum.StopChargingTransaction);
    newConnection.off(signalRKeysEnum.RefreshChargePointStatus);
    
    newConnection.on(signalRKeysEnum.RefereshChargingTransacitonData, (message) => {
      console.log("SignalR: Received transaction update", message);
      setLastMessage({ key: signalRKeysEnum.RefereshChargingTransacitonData, message });
      dispatch(chargingTransctionActions.setChargingTransaction(message));
    });

    newConnection.on(signalRKeysEnum.StopChargingTransaction, (message) => {
      console.log("SignalR: Received stop transaction", message);
      setLastMessage({ key: signalRKeysEnum.StopChargingTransaction, message });
    });

    newConnection.on(signalRKeysEnum.RefreshChargePointStatus, (message) => {
      console.log("SignalR: Received charge point status update", message);
      setLastMessage({ key: signalRKeysEnum.RefreshChargePointStatus, message });
    });
  };

  return { isConnected, lastMessage, connection: connectionRef.current };
};
